/**
 * useHoverOutline Hook
 * EXACT PORT dari ModelViewerBACKUP.jsx line 7535-7658
 * 
 * Visual feedback untuk hovered mesh:
 * - Cyan glow outline
 * - Auto fade-out effect
 * - Smooth animations
 * - Proper cleanup
 * 
 * @param {Object} sceneRef - Scene reference
 * @param {Object} hoveredMesh - Currently hovered mesh
 * @returns {Object} Hover outline ref
 */

import { useEffect, useRef } from 'react'

export function useHoverOutline(sceneRef, hoveredMesh) {
  const hoverOutlineRef = useRef(null)
  const previousHoveredMeshRef = useRef(null)

  useEffect(() => {
    if (!sceneRef.current || !window.THREE_MODULE) return

    const THREE = window.THREE_MODULE

    // Check if it's the same mesh (prevent unnecessary re-render)
    if (hoveredMesh && previousHoveredMeshRef.current) {
      if (hoveredMesh.uuid === previousHoveredMeshRef.current.uuid) {
        return // Same mesh, don't recreate outline
      }
    }

    // Update previous mesh reference
    previousHoveredMeshRef.current = hoveredMesh

    // Remove old hover outline
    if (hoverOutlineRef.current) {
      // Clear timeout if exists
      if (hoverOutlineRef.current.userData.fadeOutTimeout) {
        clearTimeout(hoverOutlineRef.current.userData.fadeOutTimeout)
      }

      if (hoverOutlineRef.current.parent) {
        hoverOutlineRef.current.parent.remove(hoverOutlineRef.current)
      }

      // Dispose materials only (geometry is shared)
      if (hoverOutlineRef.current.userData.materials) {
        hoverOutlineRef.current.userData.materials.forEach((mat) => mat.dispose())
      }
      hoverOutlineRef.current = null
    }

    // Add new hover outline
    if (hoveredMesh) {
      try {
        const hoverGroup = new THREE.Group()

        // Simple glow effect - cyan color
        const glowMat = new THREE.MeshBasicMaterial({
          color: 0x00ffff, // Bright cyan
          transparent: true,
          opacity: 0.5,
          side: THREE.FrontSide,
          depthTest: true,
          depthWrite: false,
        })

        const glowMesh = new THREE.Mesh(hoveredMesh.geometry, glowMat)
        glowMesh.scale.setScalar(1.005) // 0.5% larger untuk subtle glow
        hoverGroup.add(glowMesh)

        // Add as child so it inherits transformations
        hoveredMesh.add(hoverGroup)
        hoverOutlineRef.current = hoverGroup

        // Store materials for cleanup
        hoverOutlineRef.current.userData.materials = [glowMat]

        // Auto fade-out after 400ms
        const fadeOutDuration = 200 // Duration in ms
        const fadeStartDelay = 400 // Delay before fade starts

        const fadeOutTimeout = setTimeout(() => {
          const startTime = Date.now()
          const startOpacity = glowMat.opacity

          const fadeAnimation = () => {
            if (!glowMat || glowMat.disposed) return

            const elapsed = Date.now() - startTime
            const progress = Math.min(elapsed / fadeOutDuration, 1)

            // Smooth fade out (ease-out cubic)
            const easeOut = 1 - Math.pow(1 - progress, 3)
            glowMat.opacity = startOpacity * (1 - easeOut)

            if (progress < 1) {
              requestAnimationFrame(fadeAnimation)
            } else {
              // Hide outline after fade complete
              if (hoverGroup && hoverGroup.parent) {
                hoverGroup.visible = false
              }
            }
          }

          fadeAnimation()
        }, fadeStartDelay)

        // Store timeout ID for cleanup
        hoverOutlineRef.current.userData.fadeOutTimeout = fadeOutTimeout
      } catch (error) {
        console.error('Error creating hover outline:', error)
      }
    }

    // Cleanup
    return () => {
      if (hoverOutlineRef.current) {
        if (hoverOutlineRef.current.userData.fadeOutTimeout) {
          clearTimeout(hoverOutlineRef.current.userData.fadeOutTimeout)
        }

        if (hoverOutlineRef.current.parent) {
          hoverOutlineRef.current.parent.remove(hoverOutlineRef.current)
        }

        if (hoverOutlineRef.current.userData.materials) {
          hoverOutlineRef.current.userData.materials.forEach((mat) => mat.dispose())
        }
        hoverOutlineRef.current = null
      }
    }
  }, [sceneRef, hoveredMesh])

  return {
    hoverOutlineRef,
    previousHoveredMeshRef,
  }
}

